import { Link } from "react-router-dom";
import { contentPrinciples, faqs, submissionSteps, testimonials } from "@/mocks/leads";
import FaqAccordion from "@/components/feature/FaqAccordion";

export function ProcessSection() {
  return (
    <section className="w-full bg-foreground-950 py-16 md:py-20" id="process">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="reveal max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.25em] text-accent-400">HOW IT WORKS</span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
            一条线索从投递到呈现，要经过四步
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-background-300 md:text-base">
            我们不追求速度上的第一，而更在意每条内容能否经得起复核。以下流程对投稿人完全透明，你随时可以叫停。
          </p>
        </div>

        <ol className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {submissionSteps.map((s) => (
            <li
              key={s.step}
              className="reveal relative flex flex-col rounded-lg border border-background-50/12 bg-foreground-900/60 p-6"
            >
              <span className="font-heading text-3xl font-bold text-primary-500">{s.step}</span>
              <span className="mt-4 flex h-11 w-11 items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
                <i className={`${s.icon} text-xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-background-50">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-background-400">{s.desc}</p>
            </li>
          ))}
        </ol>

        <div className="reveal mt-10">
          <Link
            to="/guide"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            查看完整投稿指南
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function PrinciplesSection() {
  return (
    <section className="w-full bg-background-50 py-16 md:py-20" id="principles">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="reveal lg:col-span-5">
            <span className="text-xs font-semibold tracking-[0.25em] text-primary-600">OUR PRINCIPLES</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              我们如何对待每一条来源
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              爆料的价值来自真实性，而不是情绪强度。为此我们给自己定下四条不能退让的原则，
              它们决定了哪些内容会被呈现，以及以什么方式呈现。
            </p>
            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-6">
              <div className="flex items-center gap-2 text-secondary-700">
                <i className="ri-shield-user-line text-lg" aria-hidden="true" />
                <span className="font-heading text-base font-semibold">来源保护是底线</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                投稿人身份默认匿名，原始材料的访问权限仅限于当条线索的核实编辑，且不会以任何形式对外提供。
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
            {contentPrinciples.map((p) => (
              <article
                key={p.title}
                className="reveal flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-2xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function TestimonialSection() {
  return (
    <section className="w-full bg-secondary-50 py-16 md:py-20" id="voices">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="reveal max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.25em] text-secondary-700">VOICES</span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            投稿人与读者的真实反馈
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure
              key={t.name}
              className="reveal flex flex-col rounded-lg border border-secondary-200 bg-background-50 p-6"
            >
              <i className="ri-double-quotes-l text-3xl text-secondary-300" aria-hidden="true" />
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {t.text}
              </blockquote>
              <figcaption className="mt-5 border-t border-background-200 pt-4">
                <p className="font-heading text-sm font-semibold text-foreground-950">{t.name}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{t.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  return (
    <section className="w-full bg-background-100 py-16 md:py-20" id="faq">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="reveal lg:col-span-4">
            <span className="text-xs font-semibold tracking-[0.25em] text-primary-600">FAQ</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              投稿前最常被问到的几个问题
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              如果这里没有解答你的疑问，欢迎直接通过投稿表单留下联系方式，核实编辑会向你说明细节。
            </p>
            <Link
              to="/guide"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              阅读完整投稿须知
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
          <div className="reveal lg:col-span-8">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </div>
    </section>
  );
}

export function CtaSection() {
  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20dark%20textured%20background%20with%20subtle%20amber%20light%20streaks%20across%20deep%20charcoal%20paper%20surface%2C%20minimal%20cinematic%20gradient%2C%20moody%20editorial%20atmosphere%2C%20no%20text%2C%20high%20contrast&width=1600&height=800&seq=kedou-cta-2026-b2&orientation=landscape"
        alt="蝌蚪爆料 投稿入口背景"
        title="蝌蚪爆料 线索投稿入口"
        className="absolute inset-0 h-full w-full object-cover opacity-55"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/90 to-foreground-950/60" />
      <div className="relative mx-auto flex max-w-[1280px] flex-col gap-8 px-4 py-16 md:flex-row md:items-center md:justify-between md:px-6 md:py-20">
        <div className="reveal max-w-xl">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
            你手上的一条线索，可能就是答案的开始
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">
            不必等到「证据齐全」才来投稿。先把你知道的部分写下来，剩下的交由核实编辑与你一起补齐。
          </p>
        </div>
        <div className="reveal flex flex-col gap-3 sm:flex-row md:flex-col lg:flex-row">
          <Link
            to="/guide#submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-send-plane-2-line" aria-hidden="true" />
            立即投稿
          </Link>
          <Link
            to="/about"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-400/40 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            了解我们
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}